import type { Feature } from '@/userManagement/domain/entities/Feature';
import type { Permission } from '@/userManagement/domain/entities/Permission';
import { PERMISSION_FIELDS } from '@/userManagement/domain/entities/Permission';
import type { PermissionSnapshotDetail } from '@/userManagement/domain/entities/PermissionSnapshotDetail';
import type { PermissionSnapshotDetailWire, SnapshotCompareWire } from '../types/SnapshotWire';

import { mapPermissionDtoToEntity, sortFeaturesForPermissionMatrix } from './permissionMapper';

export function mapSnapshotDetailWireToEntity(wire: PermissionSnapshotDetailWire): PermissionSnapshotDetail {
  const rows = Array.isArray(wire.permissions) ? wire.permissions : [];
  return {
    id: wire.id,
    label: wire.label,
    asOfDate: wire.asOfDate,
    userCount: wire.userCount,
    savedBy: wire.savedBy,
    savedAt: wire.savedAt,
    permissions: rows.map((p) => mapPermissionDtoToEntity(p))
  };
}

export function mapCompareWireToPermissionsPair(wire: SnapshotCompareWire): {
  snapshotPermissions: ReturnType<typeof mapPermissionDtoToEntity>[];
  livePermissions: ReturnType<typeof mapPermissionDtoToEntity>[];
} {
  const snap = Array.isArray(wire.snapshotPermissions) ? wire.snapshotPermissions : [];
  const live = Array.isArray(wire.livePermissions) ? wire.livePermissions : [];
  return {
    snapshotPermissions: snap.map((p) => mapPermissionDtoToEntity(p)),
    livePermissions: live.map((p) => mapPermissionDtoToEntity(p))
  };
}

function denyAllPermissionFrom(meta: Permission): Permission {
  return {
    ...meta,
    allowRead: false,
    allowWrite: false,
    allowDelete: false,
    allowCentralAccess: false,
    allowExcelExport: false
  };
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (value != null && value.trim() !== '') {
      return value.trim();
    }
  }
  return '';
}

/** Prefer a non-empty label from either side; fall back to `Feature {id}` so matrix rows are never blank (TROC-284). */
export function resolveCompareFeatureLabel(featureId: number, ...candidates: Array<string | null | undefined>): string {
  const picked = firstNonEmpty(...candidates);
  return picked !== '' ? picked : `Feature ${featureId}`;
}

function withResolvedLabels(row: Permission, featureId: number, other?: Permission | null): Permission {
  return {
    ...row,
    featureName: resolveCompareFeatureLabel(featureId, row.featureName, other?.featureName),
    category: firstNonEmpty(row.category, other?.category)
  };
}

/** Counts features where at least one permission flag differs between snapshot and live (missing side treated as deny-all). */
export function countSnapshotLiveChangedFeatures(snapshot: Permission[], live: Permission[]): number {
  const snapMap = new Map(snapshot.map((p) => [p.featureId, p]));
  const liveMap = new Map(live.map((p) => [p.featureId, p]));
  const ids = [...new Set([...snapMap.keys(), ...liveMap.keys()])].sort((a, b) => a - b);
  let n = 0;
  for (const fid of ids) {
    const s = snapMap.get(fid);
    const l = liveMap.get(fid);
    const sRow = s ?? (l != null ? denyAllPermissionFrom(l) : null);
    const lRow = l ?? (s != null ? denyAllPermissionFrom(s) : null);
    if (sRow == null || lRow == null) continue;
    if (PERMISSION_FIELDS.some((f) => Boolean(sRow[f]) !== Boolean(lRow[f]))) n += 1;
  }
  return n;
}

/**
 * Aligns snapshot vs live into paired {@link Permission} lists so matrix rows share feature order and union coverage.
 * Resolves blank feature names (common on apply-document rows that store only featureId + flags) — TROC-284.
 */
export function mergeSnapshotLivePermissionsForMatrix(
  snapshot: Permission[],
  live: Permission[]
): {
  snapshotPermissions: Permission[];
  livePermissions: Permission[];
} {
  const snapMap = new Map(snapshot.map((p) => [p.featureId, p]));
  const liveMap = new Map(live.map((p) => [p.featureId, p]));
  const ids = [...new Set([...snapMap.keys(), ...liveMap.keys()])].sort((a, b) => a - b);
  const snapshotPermissions: Permission[] = [];
  const livePermissions: Permission[] = [];
  for (const fid of ids) {
    const s = snapMap.get(fid);
    const l = liveMap.get(fid);
    if (s && l) {
      snapshotPermissions.push(withResolvedLabels(s, fid, l));
      livePermissions.push(withResolvedLabels(l, fid, s));
    } else if (l && !s) {
      const liveLabeled = withResolvedLabels(l, fid);
      snapshotPermissions.push(denyAllPermissionFrom(liveLabeled));
      livePermissions.push(liveLabeled);
    } else if (s && !l) {
      const snapLabeled = withResolvedLabels(s, fid);
      snapshotPermissions.push(snapLabeled);
      livePermissions.push(denyAllPermissionFrom(snapLabeled));
    }
  }
  return { snapshotPermissions, livePermissions };
}

/**
 * Enriches compare permission rows from the feature catalog (name, category, MiscValues supports*).
 * Used by snapshot compare so List-changes rows are never blank-named (TROC-284).
 */
export function enrichPermissionsWithFeatureCatalog(permissions: Permission[], features: Feature[]): Permission[] {
  if (permissions.length === 0) {
    return permissions;
  }

  const byId = new Map(sortFeaturesForPermissionMatrix(features).map((f) => [f.id, f]));
  return permissions.map((p) => {
    const feature = byId.get(p.featureId);
    if (feature == null) {
      return {
        ...p,
        featureName: resolveCompareFeatureLabel(p.featureId, p.featureName)
      };
    }

    return {
      ...p,
      featureName: resolveCompareFeatureLabel(p.featureId, p.featureName, feature.name),
      category: firstNonEmpty(p.category, feature.category),
      supportsRead: feature.supportsRead !== false,
      supportsWrite: feature.supportsWrite !== false,
      supportsDelete: feature.supportsDelete !== false,
      supportsCentralAccess: feature.supportsCentralAccess !== false,
      supportsExcelExport: feature.supportsExcelExport !== false
    };
  });
}
